import { useState } from 'react';
import { Avatar, Badge, Box, Card, CardActionArea, Stack, Typography } from '@mui/material';
import { EmptyState } from '../../components/EmptyState';
import { SearchField } from '../../components/ListControls';
import { TabStrip } from '../../components/TabStrip';
import { ModeIcon } from '../shipments/ShipmentCard';
import type { Row } from './useCommunications';

const FILTERS = ['All Shipments', 'With Updates', 'My Follow-ups'] as const;
type Filter = (typeof FILTERS)[number];

/** The shipments that have conversations: search, three filters, one card each */
export function CommunicationsList({
  rows,
  selected,
  onSelect,
}: {
  rows: Row[];
  selected?: string;
  onSelect: (srb: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('All Shipments');
  const w = search.trim().toLowerCase();
  const shown = rows
    .filter((r) => filter === 'All Shipments' || (filter === 'With Updates' ? r.conv.unread > 0 : r.conv.followUp))
    .filter(
      (r) =>
        !w ||
        [r.conv.srb, r.ship.customer, r.ship.from.city, r.ship.to.city, r.conv.summary]
          .join(' ')
          .toLowerCase()
          .includes(w),
    );

  return (
    <Stack spacing={1.5}>
      <SearchField placeholder="Search shipments…" value={search} onChange={setSearch} />
      <TabStrip tabs={FILTERS.map((f) => ({ value: f }))} value={filter} onChange={setFilter} />
      {shown.length ? (
        <Stack spacing={1}>
          {shown.map(({ conv, ship }) => (
            <Card
              key={conv.srb}
              sx={{
                borderColor: conv.srb === selected ? 'primary.main' : undefined,
                bgcolor: conv.srb === selected ? 'action.hover' : undefined,
              }}
            >
              <CardActionArea onClick={() => onSelect(conv.srb)} sx={{ p: 1.5 }}>
                <Stack direction="row" spacing={1.5}>
                  <Avatar sx={{ bgcolor: 'action.selected', color: 'text.secondary' }}>
                    <ModeIcon mode={ship.mode} />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <Typography sx={{ fontWeight: 750 }}>{conv.srb}</Typography>
                      {conv.followUp && (
                        <Box
                          aria-label="Needs follow-up"
                          sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }}
                        />
                      )}
                      <Box sx={{ flex: 1 }} />
                      <Typography variant="caption" color="text.secondary">
                        {conv.last}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {ship.from.city} → {ship.to.city} ({ship.mode})
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {conv.summary}
                    </Typography>
                  </Box>
                  {conv.unread > 0 && (
                    <Badge badgeContent={conv.unread} color="primary" sx={{ alignSelf: 'center', mr: 1 }} />
                  )}
                </Stack>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      ) : (
        <EmptyState>No shipments match.</EmptyState>
      )}
    </Stack>
  );
}
